/**
 * Web component for tab-based filtering of product accessories.
 * Filters slides within a single carousel by toggling hidden on slideshow-slide elements.
 * Implements WAI-ARIA Tabs Pattern with keyboard navigation.
 *
 * Lazy-Hydration: Cards beyond the initial render limit are server-rendered as
 * lightweight placeholders containing a <template> with the full card markup.
 * Hydration is triggered by viewport proximity, idle time, or hard interactions
 * (tab click, keyboard activation, swipe).
 */
class ProductAccessoriesTabs extends HTMLElement {
    #hydrationPromise = null
    #viewportObserver = null

    connectedCallback() {
        this.addEventListener('click', this.#handleClick.bind(this))
        this.addEventListener('keydown', this.#handleKeydown.bind(this))

        if (this.querySelector('[data-lazy-card]')) {
            this.#schedulePrefetchHydration()
            this.#observeSwipe()
        }
    }

    disconnectedCallback() {
        this.#viewportObserver?.disconnect()
        this.#viewportObserver = null
    }

    get #tabs() {
        return [...this.querySelectorAll('[role="tab"]')]
    }

    #schedulePrefetchHydration() {
        if (!('IntersectionObserver' in window)) {
            this.#hydrate()
            return
        }

        this.#viewportObserver = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return
                this.#viewportObserver?.disconnect()
                this.#viewportObserver = null

                if ('requestIdleCallback' in window) {
                    window.requestIdleCallback(() => this.#hydrate(), { timeout: 1500 })
                } else {
                    setTimeout(() => this.#hydrate(), 1)
                }
            },
            { rootMargin: '400px 0px' }
        )

        this.#viewportObserver.observe(this)
    }

    #observeSwipe() {
        const carousel = this.querySelector('slideshow-component')
        if (!carousel) return

        const triggerHydration = () => {
            carousel.removeEventListener('pointerdown', triggerHydration)
            carousel.removeEventListener('scroll', triggerHydration, { capture: true })
            this.#hydrate()
        }

        carousel.addEventListener('pointerdown', triggerHydration, { once: true, passive: true })
        carousel.addEventListener('scroll', triggerHydration, { once: true, capture: true, passive: true })
    }

    #hydrate() {
        if (this.#hydrationPromise) return this.#hydrationPromise

        this.#hydrationPromise = new Promise((resolve) => {
            const lazyItems = this.querySelectorAll('[data-lazy-card]')
            if (lazyItems.length === 0) {
                resolve()
                return
            }

            const supportsSetHTMLUnsafe = typeof Element.prototype.setHTMLUnsafe === 'function'

            for (const item of lazyItems) {
                const scriptEl = item.querySelector(':scope > script[type="text/template"]')
                if (!scriptEl) continue

                const html = scriptEl.textContent.replaceAll('<!--SCRIPT-END-->', '</script>')

                if (supportsSetHTMLUnsafe) {
                    item.setHTMLUnsafe(html)
                } else {
                    item.innerHTML = html
                }

                item.removeAttribute('data-lazy-card')
                item.classList.remove('resource-list__item--lazy')
            }

            resolve()
        })

        return this.#hydrationPromise
    }

    #handleClick(event) {
        const tab = event.target.closest('[role="tab"]')
        if (!tab) return
        this.#hydrate().then(() => this.#activateTab(tab))
    }

    #handleKeydown(event) {
        const tab = event.target.closest('[role="tab"]')
        if (!tab) return

        const tabs = this.#tabs
        const index = tabs.indexOf(tab)

        let targetIndex
        switch (event.key) {
            case 'ArrowRight':
                targetIndex = (index + 1) % tabs.length
                break
            case 'ArrowLeft':
                targetIndex = (index - 1 + tabs.length) % tabs.length
                break
            case 'Home':
                targetIndex = 0
                break
            case 'End':
                targetIndex = tabs.length - 1
                break
            default:
                return
        }

        event.preventDefault()
        this.#hydrate().then(() => {
            tabs[targetIndex].focus()
            this.#activateTab(tabs[targetIndex])
        })
    }

    #activateTab(selectedTab) {
        const category = selectedTab.dataset.category

        for (const tab of this.#tabs) {
            const isSelected = tab === selectedTab
            tab.setAttribute('aria-selected', String(isSelected))
            tab.setAttribute('tabindex', isSelected ? '0' : '-1')
        }

        const panelId = selectedTab.getAttribute('aria-controls')
        const panel = this.querySelector(`#${panelId}`)
        if (panel) {
            panel.setAttribute('aria-labelledby', selectedTab.id)
        }

        this.#filterSlides(category)
    }

    #filterSlides(category) {
        const items = this.querySelectorAll('.resource-list__item[data-category]')

        for (const item of items) {
            const slide = item.closest('slideshow-slide')
            if (!slide) continue

            if (category === 'all' || item.dataset.category === category) {
                slide.removeAttribute('hidden')
            } else {
                slide.setAttribute('hidden', '')
            }
        }

        const slideshow = this.querySelector('slideshow-component')
        if (slideshow?.select) {
            slideshow.select(0, undefined, { animate: false })
        }
    }
}

if (!customElements.get('product-accessories-tabs')) {
    customElements.define('product-accessories-tabs', ProductAccessoriesTabs)
}
